import { useEffect, useState } from 'react';
import { Card } from '../../components/Card';
import MicrophoneSelectionScreen from './MicrophoneSelectionScreen';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { changeAppMicAccess, changeFileSpeed, changePrecheckApiError, changeSelectedMic } from '../../redux/appConfigSlice';
import { Text } from '../../components/Text';
import SilenceCheckScreen from './SilenceCheckScreen';
import SpeakCheckScreen, { ISpeakCheckRules } from './SpeakCheckScreen';
import classes from './MicrophoneSelection.module.css';
import { MicrophoneErrorCard } from '../../components/MicrophoneErrorCard/MicrophoneErrorCard';
import { isHeadsetMicrophone } from '../../service/MicService';
import { Loader } from '../../components/Loader';
import { getDownloadFile, fetchTestRules, savePrecheckLogs } from '../../api/authApis';
import { ErrorCard } from '../../components/ErrorCard';
import { pushFaroEvent } from '../../faroSession';

interface MicrophoneSelectionMainProps {}

interface MicrophoneErrorScreenProps {
    isMicAccessEnabled: boolean;
    loading: boolean;
}

/* Main component for the microphone selection screen */
export default function MicrophoneSelectionMain(props: Readonly<MicrophoneSelectionMainProps>) {
    const {
        appMicAccess,
        selectedMic,
        silenceMeanValue,
        testTaker: testTakerDetails,
        appFontSize: fontSize,
        isSilent,
        fileSpeed,
        soundCheckScreenAudio,
        appTheme,
        speakCheckScreenDetails,
        precheckApiError,
        insperaDetails
    } = useSelector((state: RootState) => state.appConfigSlice);
    const dispatch = useDispatch<AppDispatch>();
    const [micAccess, setMicAccess] = useState(appMicAccess);

    const [isTimerFinished, setIsTimerFinished] = useState<boolean>(false);
    const [isSpeakCheckFinished, setIsSpeakCheckFinished] = useState<boolean>(false);
    const [downloadSpeed, setDownloadSpeed] = useState('');
    const [errorCode, setErrorCode] = useState<string>('');
    function causeOfError() {
        let error = null;
        if (selectedMic?.label.trim() === '' && appMicAccess) {
            error = 'Microphone Notfound';
        } else if (isSilent) {
            error = 'Threshold not passed';
        } else if (precheckApiError) {
            error = precheckApiError;
        }
        return error;
    }
    let errorMsg = causeOfError();
    let uniqueId = testTakerDetails?.testTakerSessionId;
    let uniqueSessionId = uniqueId.replace(/[:.;]/g, '_');
    let fileName = `preCheckLog_${uniqueSessionId}.txt`;
    const payload = {
        testTakerId: testTakerDetails?.testTakerId,
        uniqueSessionId: testTakerDetails?.testTakerSessionId,
        centerName: testTakerDetails?.testTakerCenterName,
        language: testTakerDetails?.testTakerLanguage,
        status: 'Prechecks fail',
        productType: insperaDetails?.product,
        fileName: fileName,
        preCheckLog: {
            fontSize: fontSize,
            contrast: appTheme,
            speckerCheckScreenAudio: soundCheckScreenAudio ? 'played' : 'not-played',
            micCheck: selectedMic?.label,
            uploadSpeed: fileSpeed?.uploadSpeed,
            downloadSpeed: fileSpeed?.downloadSpeed || downloadSpeed,
            thresholdValue: silenceMeanValue,
            filePathOfRecording: speakCheckScreenDetails?.responseFilePath,
            duration: speakCheckScreenDetails?.duration,
            error: errorMsg,
        },
    };

    useEffect(() => {
        if (errorMsg) {
            pushFaroEvent('precheck_failed', { reason: errorMsg });
            let res = savePrecheckLogs(testTakerDetails, payload);
        }
    }, [errorMsg]);

    useEffect(() => {
        if (isSpeakCheckFinished) {
            pushFaroEvent('precheck_completed');
        }
    }, [isSpeakCheckFinished]);

    useEffect(() => {
        let isCancelled = false;
        const startDownloadSpeed = async () => {
            try {
                const result = await fetchDownloadFile();
                if (!isCancelled && result) {
                    setDownloadSpeed(`${result.avgSpeed.toFixed(2)} Mbps`);
                }
            } catch (error) {
                if (!isCancelled) {
                    setDownloadSpeed('');
                }
            }
        };
        startDownloadSpeed();
        return () => {
            isCancelled = true;
        };
    }, []);

    const rawStringToMp3Blob = (rawstr: any) => {
        const byteArray = new Uint8Array([...rawstr].map((char) => char.charCodeAt(0)));
        return new Blob([byteArray], { type: 'audio/mpeg' });
    };

    const fetchDownloadFile = async () => {
        const iterations = 1;
        let totalSpeed = 0;
        let totalSizeInKB = 0;
        const speeds: number[] = [];

        for (let i = 0; i < iterations; i++) {
            try {
                const startTime = performance.now();
                const res = await getDownloadFile(
                    testTakerDetails.testTakerSessionId,
                    testTakerDetails.testTakerId,
                    testTakerDetails.testTakerAccessToken,
                );
                const endTime = performance.now();

                const mp3Blob = rawStringToMp3Blob(res.data);
                const fileSizeInBytes = mp3Blob.size;
                const durationInSeconds = (endTime - startTime) / 1000;

                const speedMbps = fileSizeInBytes / durationInSeconds / 125000;
                speeds.push(speedMbps);
                totalSpeed += speedMbps;
                totalSizeInKB += fileSizeInBytes / 1024;
            } catch (error) {
                console.error(`Download failed on iteration ${i + 1}`, error);
                dispatch(changePrecheckApiError('Error during Download Api'));
            }
        }
        const avgSpeed = speeds.length > 0 ?  totalSpeed / speeds.length: 0;
        pushFaroEvent('download_speed_check_completed', { 
            avgSpeed: avgSpeed.toFixed(2),
            avgFileSizeKB: (totalSizeInKB / iterations).toFixed(2)
        });
        dispatch(changeFileSpeed({
            downloadSpeed: avgSpeed.toFixed(2),
        }));
        return { avgSpeed };
    };

    const [rules, setRules] = useState<ISpeakCheckRules>({
        silenceBeforeRetry: 0,
        maxSpeechTime: 0,
        silenceRequiredForComplete: 0,
        samplePhrase: '',
    });

    /* Check whether user has provided permission for microphones */
    function checkMicrophoneAccess() {
        window.navigator?.mediaDevices
            ?.getUserMedia({ audio: true })
            .then(function (stream) {
                dispatch(changeAppMicAccess(true));
                pushFaroEvent('microphone_permission_granted');
                stream.getTracks().forEach((track) => track.stop());
            })
            .catch(function (err) {
                pushFaroEvent('mic_permission_denied', {
                    error: String(err?.name ?? err?.message ?? err),
                });
                dispatch(changeAppMicAccess(false));
            });
    }

    useEffect(() => {
        fetchTestRules(testTakerDetails).then(
            (response: { preCheck: ISpeakCheckRules; errorCode?: string; message?: string }) => {
                if (response.errorCode) {
                    setErrorCode(response.errorCode);
                    console.error(response.message);
                } else {
                    setRules(response.preCheck);
                }
            },
        );
    }, []);

    useEffect(() => {
        if (!appMicAccess) {
            checkMicrophoneAccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeviceChange = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const micList = devices.filter((d) => d.kind === 'audioinput');
            let filteredMicList: any = [];
            pushFaroEvent('microphone_list_updated_on_change', { count: String(micList.length) });
            micList.forEach((mic) => {
                if (/^[0-9a-f]+$/i.test(mic.deviceId) && isHeadsetMicrophone(mic.label)) {
                    filteredMicList.push({
                        label: mic.label,
                        deviceId: mic.deviceId,
                        groupId: mic.groupId,
                    });
                }
            });
            if (filteredMicList.length != 0) {
                //Set mic access
                setMicAccess(appMicAccess);

                dispatch(
                    changeSelectedMic({
                        deviceId: filteredMicList[0].deviceId,
                        label: filteredMicList[0].label,
                    }),
                );
            } else {
                dispatch(
                    changeSelectedMic({
                        deviceId: '',
                        label: '',
                    }),
                );
            }
        } catch (error) {
            console.error('Error detecting device changes:', error);
        }
    };

    useEffect(() => {
        handleDeviceChange();
        navigator.mediaDevices.ondevicechange = handleDeviceChange;
        return () => {
            navigator.mediaDevices.ondevicechange = null; // Cleanup event listener
        };
    }, [appMicAccess]);

    const getMicList = () => {
        return selectedMic?.deviceId.trim() !== '' && !isTimerFinished ? (
            <>
                <Card className={classes.micSelection}>
                    <MicrophoneSelectionScreen />
                </Card>

                <Card className={classes.silenceCheck}>
                    <SilenceCheckScreen
                        fontSize={fontSize}
                        setIsTimerFinished={setIsTimerFinished}
                        selectedMic={selectedMic?.deviceId}
                        selectedMicLabel={selectedMic?.label}
                        rulesIsSet={rules.samplePhrase !== ''}
                    />
                </Card>
            </>
        ) : null;
    };

    return (
        <div className={classes.container} data-testid="microphone-check-container">
            {errorCode ? (
                <ErrorCard errorCode={errorCode} />
            ) : (
                <>
                    {!isTimerFinished &&
                        !isSpeakCheckFinished &&
                        (!appMicAccess ? (
                            <Card className={classes.loader}>
                                <Loader id={'test'} micAccess={true} />
                            </Card>
                        ) : (
                            getMicList()
                        ))}
                    {/* {display errorcard when no micAccess selecte} */}
                    {selectedMic?.deviceId.trim() === '' && appMicAccess ? (
                        <MicrophoneErrorCard noMicAccess={false} />
                    ) : null}

                    {isTimerFinished && !isSpeakCheckFinished && selectedMic?.deviceId.trim() !== '' ? (
                        <Card className={classes.speakCheck}>
                            <SpeakCheckScreen
                                fontSize={fontSize}
                                silenceMeanValue={silenceMeanValue}
                                selectedMic={selectedMic}
                                setIsSpeakCheckFinished={setIsSpeakCheckFinished}
                                downLoadSpeed={downloadSpeed}
                                rules={rules}
                            />
                        </Card>
                    ) : null}
                    {isSpeakCheckFinished ? (
                        <Card className={classes.speakCheck}>
                            <p>
                                <Text text="Thank you. Your microphone is working fine." />
                            </p>
                            <p>
                                <Text text="Click <b>Next</b> to move to the next part." />
                            </p>
                        </Card>
                    ) : (
                        ''
                    )}
                </>
            )}
        </div>
    );
}

/* Display an error message when the user denies microphone access */
export const MicrophoneErrorScreen = (props: MicrophoneErrorScreenProps) => {
    const { isMicAccessEnabled, loading } = props;

    return (
        <>
            <p>
                <Text text="Checking microphone access" variant="heading" fontWeight="semibold" />
            </p>

            {loading && (
                <p>
                    <Text text="Please wait" fontWeight="semibold" />
                </p>
            )}
            {!loading && !isMicAccessEnabled && (
                <>
                    <p>
                        <Text text="Microphone access denied by user. Unable to proceed with test." />
                    </p>
                    <p>
                        <Text text="Please allow access to your microphone and refresh the page." />
                    </p>
                </>
            )}
        </>
    );
};
